import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Wallet, ArrowLeftRight, CreditCard, Landmark,
  Bitcoin, Receipt, Users, ShieldCheck, LogOut, BarChart3,
} from "lucide-react";

const navCustomer = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/app/accounts", label: "Accounts", icon: Wallet },
  { to: "/app/transfer", label: "Transfer", icon: ArrowLeftRight },
  { to: "/app/cards", label: "Cards", icon: CreditCard },
  { to: "/app/loans", label: "Loans", icon: Landmark },
  { to: "/app/crypto", label: "Crypto", icon: Bitcoin },
  { to: "/app/transactions", label: "Activity", icon: Receipt },
];

const navAdmin = [
  { to: "/admin", label: "Metrics", icon: BarChart3, exact: true },
  { to: "/admin/users", label: "Customers", icon: Users },
  { to: "/admin/loans", label: "Loan Desk", icon: Landmark },
];

export const AppShell = ({ children, mode = "customer" }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = mode === "admin" ? navAdmin : navCustomer;

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] grain">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to={mode === "admin" ? "/admin" : "/app"} className="flex items-center gap-3" data-testid="app-logo-link">
            <div className="h-8 w-8 bg-[hsl(var(--primary))] grid place-items-center">
              <span className="text-white font-display text-lg leading-none">N</span>
            </div>
            <div className="font-display text-lg tracking-tight">NASHUKA</div>
            <div className="overline ml-2 hidden md:block">
              {mode === "admin" ? "Operator Console" : "Personal Banking"}
            </div>
          </Link>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && mode !== "admin" && (
              <NavLink to="/admin" className="overline hover:text-[hsl(var(--primary))]" data-testid="link-admin-console">
                <ShieldCheck className="inline w-4 h-4 mr-1" /> Operator
              </NavLink>
            )}
            {mode === "admin" && (
              <NavLink to="/app" className="overline hover:text-[hsl(var(--primary))]" data-testid="link-customer-app">
                Customer App
              </NavLink>
            )}
            <div className="hidden md:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="overline">{user?.role}</span>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="border border-[hsl(var(--border))] px-3 py-2 text-sm hover:bg-[hsl(var(--surface-alt))] flex items-center gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-12 gap-8">
        <aside className="col-span-12 lg:col-span-2">
          <div className="overline mb-4">Navigate</div>
          <nav className="flex flex-col">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-2.5 border-l-2 pl-3 text-sm transition-colors ${
                    isActive
                      ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] font-medium"
                      : "border-transparent text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-main))]"
                  }`
                }
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <it.icon className="w-4 h-4" /> {it.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="col-span-12 lg:col-span-10 min-w-0">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
